import { Grid } from "@material-ui/core";
import arrayMove from "array-move";
import moment from "moment-timezone";
import { useCallback, useEffect, useState } from "react";
import { BooleanInput, CheckboxGroupInput, Create, CreateButton, Datagrid, Edit, EditButton, Error, Filter, FormDataConsumer, List, Loading, SaveButton, SimpleForm, TextField, TextInput, Toolbar, TopToolbar, required, useDataProvider, useNotify, useRecordContext } from "react-admin";
import { Field, useForm } from "react-final-form";
import { enableMpmPlatformOrdering } from "../../constant";
import { PagePathInput } from "../page-row-paths/components/page-path-input";
import { SeoDescriptionInput } from "../component/seo-description-text-input";
import { ImageDropzone } from "./components/image-dropzone";
import { defaultPlatformColumns, MpmRowsGrid, validateMpmRowsGridPlatformOrders } from "./components/mpm-rows-grid";

const PageTitle = ({ record }) => {
  return <span>Page {record ? `${record.name_tc}` : ""}</span>;
};

const PageFilter = (props) => (
  <Filter {...props}>
    <TextInput label="ID" source="_id" alwaysOn />
    <TextInput label="Name in TC" source="name_tc" alwaysOn />
    <TextInput label="Name in EN" source="name_en" alwaysOn />
    <TextInput label="Menu ID" source="category_id" alwaysOn />
  </Filter>
);

const PageEditToolbar = (props) => {
  const form = useForm();
  const notify = useNotify();

  const handleSubmitWithRedirect = useCallback(() => {
    if (enableMpmPlatformOrdering) {
      const errors = validateMpmRowsGridPlatformOrders(form.getState().values);

      if (errors.platform_orders) {
        notify(errors.platform_orders, "warning");
        return;
      }
    }

    form.submit();
  }, [form, notify]);

  return (
    <Toolbar alwaysEnableSaveButton={true} {...props}>
      <SaveButton handleSubmitWithRedirect={handleSubmitWithRedirect} />
    </Toolbar>
  );
};

const HomepageList = ({ profileClass }) => {
  const dataProvider = useDataProvider();

  useEffect(() => {
    dataProvider
      .getMpmHomepage(profileClass)
      .then(({ data }) => {
        window.location.href = "/admin/#/" + (profileClass == "kids" ? "kids-mpm-home-page" : "mpm-home-page") + "/" + data.id;
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  return <></>;
};

export const HomepageEdit = (props) => {
  return <EditX {...props} />;
};

const ListAction = () => {
  return (
    <TopToolbar>
      <CreateButton />
    </TopToolbar>
  );
};

export const PageList = (props) => {
  return (
    <List {...props} sort={{ field: "_id", order: "DESC" }} bulkActionButtons={false} filters={<PageFilter />} actions={<ListAction />}>
      <Datagrid>
        <TextField source="id" />
        <TextField label="Name in TC" source="name_tc" />
        <TextField label="Name in EN" source="name_en" />
        <TextField label="Menu ID" source="category_id" />
        <TextField label="Page Path" source="path" />
        <EditButton />
      </Datagrid>
    </List>
  );
};

const sortRowsByInitialStbOrder = (rows) => {
  return rows
    .map((row, index) => ({
      row,
      index,
      stbOrder: row.platform_orders?.stb === undefined || row.platform_orders?.stb === null || row.platform_orders?.stb === "" ? null : Number(row.platform_orders.stb),
    }))
    .sort((a, b) => {
      const aHasStbOrder = a.stbOrder !== null && !Number.isNaN(a.stbOrder);
      const bHasStbOrder = b.stbOrder !== null && !Number.isNaN(b.stbOrder);

      if (aHasStbOrder && bHasStbOrder) {
        return a.stbOrder - b.stbOrder || a.index - b.index;
      }

      if (aHasStbOrder) {
        return -1;
      }

      if (bHasStbOrder) {
        return 1;
      }

      return a.index - b.index;
    })
    .map(({ row }) => row);
};

const PageMpmRowsGrid = ({ profileClass }) => {
  const data = useRecordContext();
  const form = useForm();
  const dataProvider = useDataProvider();
  const [rows, setRows] = useState();
  const [templates, setTemplates] = useState();
  const [platformColumns, setPlatformColumns] = useState(defaultPlatformColumns);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setRows((rows) => {
      var updatedRows = arrayMove(rows, oldIndex, newIndex);
      var updatedRowIds = updatedRows.map((r) => r.id);
      form.change("rows", updatedRowIds);
      return updatedRows;
    });
  };

  const onDelete = (rowId) => {
    setRows((rows) => {
      var updatedRows = rows.filter((row) => row.id !== rowId);
      var updatedRowIds = updatedRows.map((r) => r.id);
      form.change("rows", updatedRowIds);
      return updatedRows;
    });
  };

  useEffect(() => {
    dataProvider
      .getList("mpm-row-templates", {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "name", order: "ASC" },
        filter: {},
      })
      .then(({ data }) => {
        setTemplates(data);
      })
      .catch((error) => {
        setError(error);
      });
  }, []);

  useEffect(() => {
    const loadRows = async () => {
      try {
        var rowIds = data.rows || [];
        const { data: mpmRowData } = await dataProvider.getMany("mpm-rows", { ids: rowIds });

        var rows = [];
        for (var rowId of rowIds) {
          var x = mpmRowData.find((row) => row.id == rowId);
          if (x != null) {
            rows.push(x);
          }
        }

        if (enableMpmPlatformOrdering) {
          const { data: rowPlatformsData } = await dataProvider.getMpmRowsOrderByPlatform(data.id);
          const updatedPlatformColumns = defaultPlatformColumns.map((platformColumn) => ({
            ...platformColumn,
            id: rowPlatformsData.find((rowPlatformData) => rowPlatformData.platform == platformColumn.key)?.id || "",
          }));

          const platformOrders = {};
          for (const row of rows) {
            row.platform_orders = {};
            platformOrders[row.id] = {};

            for (const rowPlatformData of rowPlatformsData) {
              const index = rowPlatformData.rows.findIndex((r) => r == row.id);
              if (index >= 0) {
                row.platform_orders[rowPlatformData.platform] = index + 1;
                platformOrders[row.id][rowPlatformData.platform] = index + 1;
              }
            }
          }

          form.change("platform_orders", platformOrders);
          setPlatformColumns(updatedPlatformColumns);
        }

        setRows(enableMpmPlatformOrdering ? sortRowsByInitialStbOrder(rows) : rows);
        setLoading(false);
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };

    loadRows();
  }, [data.id, (data.rows || []).length]);

  if (loading) return <Loading />;
  if (error) return <Error />;
  if (!rows) return null;
  if (!templates) return null;

  return (
    <MpmRowsGrid //
      profileClass={profileClass}
      pageId={data.id}
      isHomepage={data.is_homepage}
      templates={templates}
      rows={rows}
      form={form}
      onSortEnd={onSortEnd}
      onDelete={onDelete}
      platformColumns={platformColumns}
      showPlatformOrders={enableMpmPlatformOrdering}
      showReorderButton={!enableMpmPlatformOrdering}
    />
  );
};

const CategoryGrid = () => {
  const record = useRecordContext();

  const dataProvider = useDataProvider();
  // const [categories, setCategories] = useState();
  const [category, setCategory] = useState();

  useEffect(() => {
    if (!record.category_id) {
      return;
    }

    dataProvider.getMenuTreeCategoryById(record.category_id).then(({ data }) => {
      setCategory(data);
    });
  }, []);

  if (!category) {
    return <></>;
  }

  var parentCategory;
  var thisCategory;

  //Max 2 level
  if (category.menu_tree) {
    for (var firstLevel of category.menu_tree) {
      if (firstLevel.category_id == record.category_id) {
        thisCategory = firstLevel;
        break;
      }

      if (firstLevel.categories) {
        for (var secondLevel of firstLevel.categories) {
          if (secondLevel.category_id == record.category_id) {
            thisCategory = secondLevel;
            parentCategory = firstLevel;
            break;
          }
        }
      }
    }
  }

  var status, parentTc, parentEn, categoryTc, categoryEn;

  var datetime = moment(category.start_time).format("YYYY-MM-DD HH:mm");
  if (category.status == "inactive") {
    status = `This page is inside an inactive menu set [${category.description}] which will be effective on [${datetime}]`;
  } else if (category.status == "active") {
    status = `This page is inside a currently active menu set [${category.description}]`;
  } else if (category.status == "expired") {
    status = `This page is inside an inactive menu set [${category.description}] which is expired on [${datetime}]`;
  }

  if (parentCategory) {
    parentTc = `Parent in TC: ${parentCategory.name_tc}`;
    parentEn = `Parent in EN: ${parentCategory.name_en}`;
  }
  if (thisCategory) {
    categoryTc = `Category Name in TC: ${thisCategory.name_tc}`;
    categoryEn = `Category Name in EN: ${thisCategory.name_en}`;
  }

  if (parentCategory && thisCategory) {
    return (
      <>
        {status}
        <br />
        <br />
        Menu ID: {record.category_id}
        <br />
        <br />
        {parentTc}
        <br />
        {parentEn}
        <br />
        <br />
        {categoryTc}
        <br />
        {categoryEn}
      </>
    );
  } else if (thisCategory) {
    return (
      <>
        {status}
        <br />
        <br />
        Menu ID: {record.category_id}
        <br />
        <br />
        {categoryTc}
        <br />
        {categoryEn}
      </>
    );
  }

  return (
    <>
      {status}
      <br />
      <br />
      Menu ID: {record.category_id}
    </>
  );
};

const EditX = ({ profileClass, ...props }) => {
  const notify = useNotify();
  const onFailure = (error) => {
    notify(error.message.toString(), "warning");
  };
  const onSuccess = () => {
    window.location.reload(false);
  };

  return (
    <Edit onSuccess={onSuccess} onFailure={onFailure} mutationMode="pessimistic" {...props} title={<PageTitle />}>
      <SimpleForm toolbar={<PageEditToolbar />}>
        <Field name="pc" component="input" type="hidden" defaultValue={profileClass} />
        <Grid container spacing={1} fullWidth>
          <Grid item sm={12} md={12}>
            <CategoryGrid />
          </Grid>

          {/* <Grid item sm={12} md={12}>
            <TextInput source="name" validate={required()} fullWidth />
          </Grid> */}
          <Grid item sm={12} md={12}>
            <TextInput label="Name in TC" source="name_tc" validate={required()} fullWidth />
          </Grid>
          <Grid item sm={12} md={12}>
            <TextInput label="Name in EN" source="name_en" validate={required()} fullWidth />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <PagePathInput disabled />
          </Grid>

          <Grid item sm={12} md={12}>
            <TextInput label="Page title in TC (SEO)" source="title_tc" fullWidth />
          </Grid>
          <Grid item sm={12} md={12}>
            <TextInput label="Page title in EN (SEO)" source="title_en" fullWidth />
          </Grid>

          <Grid item sm={12} md={12}>
            <SeoDescriptionInput label="SEO Description in TC (within 85 char)" source="seo_desc_tc" maxLength={85} multiline />
          </Grid>
          <Grid item sm={12} md={12}>
            <SeoDescriptionInput label="SEO Description in EN (within 170 char)" source="seo_desc_en" maxLength={170} multiline />
          </Grid>
        </Grid>
        <FormDataConsumer>
          {({ formData }) =>
            formData.is_homepage == false && (
              <>
                <Grid container spacing={1} fullWidth>
                  <Grid item sm={12} md={6}>
                    <BooleanInput source="call_ad" label="Call ad" />
                  </Grid>

                  <Grid item sm={12} md={6}>
                    <BooleanInput source="made_for_kid" label="Made for kid" />
                  </Grid>
                </Grid>

                <Grid container spacing={1} fullWidth>
                  <Grid item sm={12} md={12}>
                    <CheckboxGroupInput source="template" label="Template" choices={[{ id: "is_olympic", name: "Olympic" }]} />
                  </Grid>
                </Grid>

                <Grid container spacing={1} fullWidth>
                  <Grid item xs={6} sm={6} md={6}>
                    <ImageDropzone source="zoneimage_app_tc" sourceBase="zoneimage_app_tc_base64" label="ZoneImage App in TC" />
                  </Grid>

                  <Grid item xs={6} sm={6} md={6}>
                    <ImageDropzone source="zoneimage_app_en" sourceBase="zoneimage_app_en_base64" label="ZoneImage App in EN" />
                  </Grid>

                  <Grid item xs={6} sm={6} md={6}>
                    <ImageDropzone source="zoneimage_stb_tc" sourceBase="zoneimage_stb_tc_base64" label="ZoneImage Stb in TC" />
                  </Grid>

                  <Grid item xs={6} sm={6} md={6}>
                    <ImageDropzone source="zoneimage_stb_en" sourceBase="zoneimage_stb_en_base64" label="ZoneImage Stb in EN" />
                  </Grid>

                  <Grid item xs={6} sm={6} md={6}>
                    <ImageDropzone source="zoneimage_web_tc" sourceBase="zoneimage_web_tc_base64" label="ZoneImage Web in TC" />
                  </Grid>

                  <Grid item xs={6} sm={6} md={6}>
                    <ImageDropzone source="zoneimage_web_en" sourceBase="zoneimage_web_en_base64" label="ZoneImage Web in EN" />
                  </Grid>
                </Grid>
              </>
            )
          }
        </FormDataConsumer>

        <PageMpmRowsGrid profileClass={profileClass} />
        <Field name="is_homepage" component="input" type="hidden" />
      </SimpleForm>
    </Edit>
  );
};

const PageEdit = ({ profileClass, ...props }) => {
  return <EditX profileClass={profileClass} {...props} />;
};

export const PageCreate = (props) => {
  const notify = useNotify();
  const onFailure = (error) => {
    notify(error.message.toString(), "warning");
  };

  const NameAndPath = () => {
    const form = useForm();
    const [dummy, setDummy] = useState(1);

    const nameEnOnChange = () => {
      const n = form.getState().values.name_en;
      const n2 = n.replace(/[^A-Za-z0-9]/g, "").toLowerCase();
      form.change("path", n2);
      setDummy(dummy + 1);
    };

    return (
      <>
        <Grid item sm={12} md={12}>
          <TextInput label="Name in EN" source="name_en" validate={required()} fullWidth onChange={nameEnOnChange} />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <PagePathInput dummy={dummy} />
        </Grid>
      </>
    );
  };

  return (
    <Create {...props} onFailure={onFailure}>
      <SimpleForm redirect="edit">
        <Grid container spacing={1} fullWidth>
          {/* <Grid item sm={12} md={12}>
            <TextInput source="name" validate={required()} fullWidth />
          </Grid> */}
          <Grid item sm={12} md={12}>
            <TextInput label="Name in TC" source="name_tc" validate={required()} fullWidth />
          </Grid>

          <NameAndPath />

          <Grid item sm={12} md={12}>
            <SeoDescriptionInput label="SEO Description in TC (within 85 char)" source="seo_desc_tc" maxLength={85} multiline />
          </Grid>
          <Grid item sm={12} md={12}>
            <SeoDescriptionInput label="SEO Description in EN (within 170 char)" source="seo_desc_en" maxLength={170} multiline />
          </Grid>
          <Grid item sm={12} md={12}>
            <TextInput label="Page title in TC (SEO)" source="title_tc" fullWidth />
          </Grid>
          <Grid item sm={12} md={12}>
            <TextInput label="Page title in EN (SEO)" source="title_en" fullWidth />
          </Grid>
        </Grid>

        <Grid container spacing={1} fullWidth>
          <Grid item sm={12} md={6}>
            <BooleanInput source="call_ad" label="Call ad" />
          </Grid>

          <Grid item sm={12} md={6}>
            <BooleanInput source="made_for_kid" label="Made for kid" />
          </Grid>
        </Grid>

        <Grid container spacing={1} fullWidth>
          <Grid item sm={12} md={12}>
            <CheckboxGroupInput source="template" label="Template" choices={[{ id: "is_olympic", name: "Olympic" }]} />
          </Grid>
        </Grid>

        <Grid container spacing={1} fullWidth>
          <Grid item xs={6} sm={6} md={6}>
            <ImageDropzone source="zoneimage_app_tc" sourceBase="zoneimage_app_tc_base64" label="ZoneImage App in TC" />
          </Grid>

          <Grid item xs={6} sm={6} md={6}>
            <ImageDropzone source="zoneimage_app_en" sourceBase="zoneimage_app_en_base64" label="ZoneImage App in EN" />
          </Grid>

          <Grid item xs={6} sm={6} md={6}>
            <ImageDropzone source="zoneimage_stb_tc" sourceBase="zoneimage_stb_tc_base64" label="ZoneImage Stb in TC" />
          </Grid>

          <Grid item xs={6} sm={6} md={6}>
            <ImageDropzone source="zoneimage_stb_en" sourceBase="zoneimage_stb_en_base64" label="ZoneImage Stb in EN" />
          </Grid>

          <Grid item xs={6} sm={6} md={6}>
            <ImageDropzone source="zoneimage_web_tc" sourceBase="zoneimage_web_tc_base64" label="ZoneImage Web in TC" />
          </Grid>

          <Grid item xs={6} sm={6} md={6}>
            <ImageDropzone source="zoneimage_web_en" sourceBase="zoneimage_web_en_base64" label="ZoneImage Web in EN" />
          </Grid>
        </Grid>

        <Field name="is_homepage" component="input" type="hidden" defaultValue={false} />
      </SimpleForm>
    </Create>
  );
};

export const GeneralHomepageList = (props) => <HomepageList profileClass="general" {...props} />;
export const KidsHomepageList = (props) => <HomepageList profileClass="kids" {...props} />;

export const GeneralHomepageEdit = (props) => <PageEdit profileClass="general" {...props} />;
export const KidsHomepageEdit = (props) => <PageEdit profileClass="kids" {...props} />;
// export const KidsPageEdit = (props) => <PageEdit profileClass="kids" {...props} />;
export const GeneralPageEdit = (props) => <PageEdit profileClass="general" {...props} />;
